// @flow
import { useMemo } from 'react';
import moment from 'moment';

import { useUnitPendingMoveInWarning } from '../../../../../hooks/data-fetching/useNaurWarnings';
import messages from './messages';

type Params = {
  unitId: ?string,
  moveInDate: any,
  isAffordableUnit: boolean,
  naurFlagEnabled: boolean,
  intl: Object,
};

type Result = {
  pendingMoveInError: ?string,
  isCheckingPendingMoveIn: boolean,
};

/**
 * Validates the applicant's scheduled move-in date against the
 * PENDING_MOVE_IN NAUR restriction for the unit's building.
 *
 * - No-ops (returns neutral state) when:
 *   - The `naur` feature flag is off (`naurFlagEnabled: false`)
 *   - The unit is affordable/LIHTC (`isAffordableUnit: true`)
 *   - No `unitId` is available
 * - Fails silently on network/5xx errors — the user is never blocked by
 *   a warning-check API failure (UX requirement #4).
 */
const usePendingMoveInValidation = ({
  unitId,
  moveInDate,
  isAffordableUnit,
  naurFlagEnabled,
  intl,
}: Params): Result => {
  const shouldFetch = !!unitId && !isAffordableUnit && naurFlagEnabled;

  const { data, isLoading, isError } = useUnitPendingMoveInWarning(
    unitId,
    shouldFetch,
  );

  const pendingMoveInError = useMemo(() => {
    // Feature is off, unit is affordable, still loading, or API errored — no error
    if (!shouldFetch || isLoading || isError) return null;

    // No PENDING_MOVE_IN restriction active for this building
    if (!data?.isImpactedByPendingMoveIn) return null;

    // Restriction is active but the satisfying household hasn't set their
    // move-in date yet — block save with a generic message (no date to compare)
    if (!data.scheduledMoveInRestrictionDate) {
      return intl.formatMessage(messages.pendingMoveInNaurGenericError);
    }

    // No applicant move-in date selected yet — nothing to validate against
    const momentMoveIn = moveInDate ? moment(moveInDate) : null;
    if (!momentMoveIn || !momentMoveIn.isValid()) return null;

    // Violation: applicant date must be STRICTLY AFTER the restriction date
    // isSameOrBefore → on or before → violation
    const isSameOrBefore = momentMoveIn.isSameOrBefore(
      moment(data.scheduledMoveInRestrictionDate),
      'day',
    );

    if (!isSameOrBefore) return null;

    return intl.formatMessage(messages.pendingMoveInNaurDateError, {
      // Pass timeZone: 'UTC' so the date-only string (e.g. "2026-05-15") is
      // not shifted to the previous day when the user's locale is behind UTC.
      restrictionDate: intl.formatDate(data.scheduledMoveInRestrictionDate, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        timeZone: 'UTC',
      }),
    });
  }, [shouldFetch, isLoading, isError, data, moveInDate, intl]);

  return {
    pendingMoveInError,
    // True only while the API call is in flight — used to disable Save (UX req #1)
    isCheckingPendingMoveIn: shouldFetch && isLoading,
  };
};

export default usePendingMoveInValidation;
